import React, { useState, useEffect } from 'react';
import { UserProfile, ProfileChangeRequest } from '../types';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, onSnapshot, query, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { safeFormatDate } from '../lib/utils';
import { Check, X, Clock, AlertCircle, CheckCircle2, XCircle, ArrowRight, UserCheck, Trash2 } from 'lucide-react';

interface ProfileChangeQueueProps {
  currentUserProfile: UserProfile | null;
  allUsers: UserProfile[];
}

export default function ProfileChangeQueue({ currentUserProfile, allUsers }: ProfileChangeQueueProps) {
  const [requests, setRequests] = useState<ProfileChangeRequest[]>([]);
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [selectedRejectRequest, setSelectedRejectRequest] = useState<ProfileChangeRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, 'profile_change_requests'),
      orderBy('submittedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      } as ProfileChangeRequest));
      setRequests(data);
    }, (error) => {
      console.warn('Profile change requests notice:', error.message);
    });

    return () => unsubscribe();
  }, []);

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const filteredRequests = requests.filter(r => {
    if (filter === 'all') return true;
    return r.status === filter;
  });

  const handleApprove = async (req: ProfileChangeRequest) => {
    if (!req.id || !req.userId) return;
    setIsProcessing(true);

    try {
      // 1. Update the user document with the requested changes
      const userRef = doc(db, 'users', req.userId);
      const changesToApply = { ...req.requestedChanges };
      
      // Clean up any undefined values
      Object.keys(changesToApply).forEach(k => {
        if ((changesToApply as any)[k] === undefined) {
          delete (changesToApply as any)[k];
        }
      });

      await updateDoc(userRef, changesToApply);

      // 2. Mark the request as approved
      const reqRef = doc(db, 'profile_change_requests', req.id);
      await updateDoc(reqRef, {
        status: 'approved',
        reviewedByUid: currentUserProfile?.uid || 'admin',
        reviewedByName: currentUserProfile ? `${currentUserProfile.firstName} ${currentUserProfile.lastName}` : 'Executive Officer',
        reviewedAt: new Date().toISOString()
      });

      toast.success(`Approved personnel updates for ${req.userName}. Record updated.`);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${req.userId}`);
      toast.error('Failed to apply profile changes. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!selectedRejectRequest?.id) return;
    setIsProcessing(true);

    try {
      const reqRef = doc(db, 'profile_change_requests', selectedRejectRequest.id);
      await updateDoc(reqRef, {
        status: 'rejected',
        reviewNotes: rejectReason.trim(),
        reviewedByUid: currentUserProfile?.uid || 'admin',
        reviewedByName: currentUserProfile ? `${currentUserProfile.firstName} ${currentUserProfile.lastName}` : 'Executive Officer',
        reviewedAt: new Date().toISOString()
      });

      toast.info(`Returned/Rejected change request for ${selectedRejectRequest.userName}.`);
      setSelectedRejectRequest(null);
      setRejectReason('');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `profile_change_requests/${selectedRejectRequest.id}`);
      toast.error('Failed to update request status.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (requestId: string) => {
    if (!window.confirm('Delete this change request record permanently?')) return;
    try {
      await deleteDoc(doc(db, 'profile_change_requests', requestId));
      toast.success('Request removed from queue history.');
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `profile_change_requests/${requestId}`);
    }
  };

  const formatValue = (val: any) => {
    if (val === undefined || val === null || val === '') return <span className="opacity-30 italic">None</span>;
    if (Array.isArray(val)) return val.join(', ') || <span className="opacity-30 italic">None</span>;
    if (typeof val === 'boolean') return val ? 'YES' : 'NO';
    return String(val);
  };

  const FIELD_LABELS: Record<string, string> = {
    firstName: 'First Name',
    lastName: 'Last Name',
    cell: 'Cell Phone',
    sccNumber: 'SCC Number',
    sfiEmail: 'SFI Email',
    dob: 'Date of Birth',
    sfiJoinDate: 'SFI Join Date',
    sfiExpireDate: 'SFI Expiration Date',
    zavalaJoinDate: 'Zavala Join Date',
    zavalaExpireDate: 'Zavala Expiration Date',
    department: 'Primary Department',
    departments: 'Departments',
    shipName: 'Assigned Ship'
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-[var(--background)] border-l-8 border-lcars-gold pl-4 py-2 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <UserCheck className="w-7 h-7 text-lcars-gold" />
            <h2 className="text-2xl font-bold text-lcars-gold uppercase tracking-tighter">
              XO Profile Review Queue
            </h2>
            {pendingCount > 0 && (
              <span className="bg-lcars-red text-[var(--foreground)] px-2.5 py-0.5 rounded-full text-xs font-bold animate-pulse">
                {pendingCount} PENDING
              </span>
            )}
          </div>
          <p className="text-xs text-[var(--foreground)]/60 font-sans tracking-normal mt-1">
            Review, verify, and approve personnel profile change requests submitted by crew members.
          </p>
        </div>

        {/* Filter Pills */}
        <div className="flex gap-1.5 bg-black/40 p-1 rounded-full border border-white/10 text-xs">
          <button
            onClick={() => setFilter('pending')}
            className={`px-3 py-1 rounded-full font-bold transition-all ${filter === 'pending' ? 'bg-lcars-gold text-black' : 'text-[var(--foreground)]/60 hover:text-[var(--foreground)]'}`}
          >
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-3 py-1 rounded-full font-bold transition-all ${filter === 'approved' ? 'bg-lcars-blue text-black' : 'text-[var(--foreground)]/60 hover:text-[var(--foreground)]'}`}
          >
            Approved ({requests.filter(r => r.status === 'approved').length})
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-3 py-1 rounded-full font-bold transition-all ${filter === 'rejected' ? 'bg-lcars-red text-[var(--foreground)]' : 'text-[var(--foreground)]/60 hover:text-[var(--foreground)]'}`}
          >
            Returned ({requests.filter(r => r.status === 'rejected').length})
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-full font-bold transition-all ${filter === 'all' ? 'bg-lcars-tan text-black' : 'text-[var(--foreground)]/60 hover:text-[var(--foreground)]'}`}
          >
            All ({requests.length})
          </button>
        </div>
      </div>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <Card className="bg-black/30 border-2 border-white/10 text-center py-12">
          <CardContent className="space-y-2">
            <CheckCircle2 className="w-12 h-12 text-lcars-blue mx-auto opacity-40" />
            <p className="text-sm font-bold uppercase tracking-widest text-[var(--foreground)]/60">
              {filter === 'pending' ? 'No pending profile change requests in the XO queue.' : 'No matching requests found.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {filteredRequests.map(req => {
            const changedKeys = Object.keys(req.requestedChanges || {});
            const isPending = req.status === 'pending';

            return (
              <Card 
                key={req.id} 
                className={`bg-black/40 border-2 ${
                  isPending ? 'border-lcars-gold shadow-lg shadow-lcars-gold/5' : 
                  req.status === 'approved' ? 'border-lcars-blue/40 opacity-80' : 'border-lcars-red/40 opacity-80'
                }`}
              >
                <CardHeader className="p-4 sm:p-5 border-b border-white/10">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-lcars-tan uppercase">
                          {req.userName}
                        </span>
                        <Badge 
                          className={`text-[0.65rem] uppercase tracking-wider font-bold ${
                            req.status === 'pending' ? 'bg-lcars-gold text-black' :
                            req.status === 'approved' ? 'bg-lcars-blue text-black' : 'bg-lcars-red text-[var(--foreground)]'
                          }`}
                        >
                          {req.status}
                        </Badge>
                      </div>
                      <div className="text-xs text-[var(--foreground)]/50 font-mono">
                        {req.userEmail} • Submitted {safeFormatDate(req.submittedAt, 'yyyy.MM.dd HH:mm', 'Recent')}
                      </div>
                    </div>

                    {isPending && (
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm"
                          disabled={isProcessing}
                          onClick={() => setSelectedRejectRequest(req)}
                          className="bg-lcars-red/20 hover:bg-lcars-red text-lcars-red hover:text-[var(--foreground)] border border-lcars-red/40 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
                        >
                          <X className="w-3.5 h-3.5" /> REJECT
                        </Button>
                        <Button 
                          size="sm"
                          disabled={isProcessing}
                          onClick={() => handleApprove(req)}
                          className="bg-lcars-gold hover:bg-lcars-gold/80 text-black text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
                        >
                          <Check className="w-3.5 h-3.5" /> APPROVE & MERGE
                        </Button>
                      </div>
                    )}

                    {!isPending && (
                      <div className="flex items-center gap-3">
                        <div className="text-right text-[0.65rem] text-[var(--foreground)]/40 font-mono">
                          Reviewed by {req.reviewedByName || 'Command'} on {safeFormatDate(req.reviewedAt || '', 'yyyy.MM.dd', '---')}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => req.id && handleDelete(req.id)}
                          className="text-[var(--foreground)]/30 hover:text-lcars-red p-1 h-auto"
                          title="Delete record"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {req.reason && (
                    <div className="mt-3 p-3 rounded bg-white/5 border border-white/10 text-xs text-[var(--foreground)]/80 font-sans normal-case">
                      <span className="text-lcars-gold font-bold uppercase tracking-widest text-[0.6rem] block mb-0.5">
                        Officer's Note:
                      </span>
                      "{req.reason}"
                    </div>
                  )}

                  {req.reviewNotes && (
                    <div className="mt-2 p-2 rounded bg-lcars-red/10 border border-lcars-red/30 text-xs text-lcars-red font-sans normal-case">
                      <span className="font-bold uppercase tracking-widest text-[0.6rem] block mb-0.5">
                        XO Review Feedback:
                      </span>
                      "{req.reviewNotes}"
                    </div>
                  )}
                </CardHeader>

                <CardContent className="p-4 sm:p-5">
                  <h4 className="text-xs font-bold text-lcars-blue uppercase tracking-widest mb-3">
                    Requested Changes Comparison ({changedKeys.length} field{changedKeys.length === 1 ? '' : 's'})
                  </h4>

                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-xs font-sans">
                      <thead>
                        <tr className="border-b border-white/10 text-[var(--foreground)]/50 uppercase tracking-widest text-[0.65rem]">
                          <th className="text-left py-2 px-3">Field</th>
                          <th className="text-left py-2 px-3">Current File Value</th>
                          <th className="text-left py-2 px-3"></th>
                          <th className="text-left py-2 px-3 text-lcars-gold">Requested New Value</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {changedKeys.map(key => {
                          const currentVal = (req.currentProfile as any)?.[key];
                          const requestedVal = (req.requestedChanges as any)?.[key];
                          const label = FIELD_LABELS[key] || key;

                          return (
                            <tr key={key} className="hover:bg-white/5 transition-colors">
                              <td className="py-2.5 px-3 font-bold uppercase tracking-wider text-[var(--foreground)]/80">
                                {label}
                              </td>
                              <td className="py-2.5 px-3 text-[var(--foreground)]/50">
                                {formatValue(currentVal)}
                              </td>
                              <td className="py-2.5 px-1 text-lcars-gold">
                                <ArrowRight className="w-3.5 h-3.5 inline" />
                              </td>
                              <td className="py-2.5 px-3 font-bold text-lcars-gold bg-lcars-gold/5 rounded">
                                {formatValue(requestedVal)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Reject Reason Modal */}
      {selectedRejectRequest && (
        <Dialog open={true} onOpenChange={() => setSelectedRejectRequest(null)}>
          <DialogContent className="bg-[#111] border-2 border-lcars-red text-[var(--foreground)] font-lcars p-6">
            <DialogHeader className="border-b border-lcars-red/30 pb-3">
              <DialogTitle className="text-xl font-bold text-lcars-red uppercase tracking-tighter">
                RETURN / REJECT PROFILE UPDATE
              </DialogTitle>
              <DialogDescription className="text-xs text-[var(--foreground)]/60 font-sans">
                Provide feedback to {selectedRejectRequest.userName} explaining why this change was not approved.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-xs uppercase font-bold text-[var(--foreground)]/80 tracking-widest">
                  Reason for Rejection / Return Note
                </label>
                <Textarea 
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="e.g., Please provide updated SFI membership proof or verify phone number format."
                  rows={3}
                  className="bg-[var(--background)] border-white/20 font-sans text-sm normal-case"
                />
              </div>
            </div>

            <DialogFooter className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <Button
                type="button"
                variant="outline"
                onClick={() => setSelectedRejectRequest(null)}
                className="border-white/20 text-xs uppercase"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirmReject}
                disabled={isProcessing}
                className="bg-lcars-red hover:bg-lcars-red/80 text-[var(--foreground)] font-bold text-xs uppercase tracking-wider"
              >
                Confirm Rejection
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
